import React from 'react';
import {
  Box,
  Dialog,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { fonts, radii } from '@/theme';
import { LogoMark, useModeTokens } from '@/common/components/ui';
import { DownloadDesktopAppPanel } from '@/common/components/DownloadDesktopAppPanel';

interface DownloadDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * The desktop agent, offered without leaving the landing page.
 *
 * The body is `DownloadDesktopAppPanel` — the same component `/download`
 * renders, so platform detection and the release URLs have one implementation.
 * Only the surface around it is new.
 *
 * Built on MUI's `Dialog` rather than the app's `DialogWindow`: that wrapper
 * paints a grey title bar belonging to the authenticated app, which reads as a
 * different product against the landing's hardware-like surface.
 */
export const DownloadDialog: React.FC<DownloadDialogProps> = ({
  open,
  onClose,
}) => {
  const t = useModeTokens();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="sm"
      fullWidth
      aria-labelledby="download-dialog-title"
      slotProps={{
        backdrop: {
          // Deliberately not mode-aware: a scrim darkens the page behind the
          // dialog, so it stays dark in light mode too.
          sx: {
            backgroundColor: t.surfaces.backdrop,
          },
        },
        paper: {
          sx: {
            borderRadius: fullScreen ? 0 : `${radii['3xl']}px`,
            border: `1px solid ${t.borders.strong}`,
            background: t.surfaces.glassMenu,
            // `modal` already carries the directional edge light and cast shadow.
            boxShadow: t.shadows.modal,
            backgroundImage: 'none',
            padding: { xs: '24px 20px 28px', sm: '30px 32px 34px' },
          },
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '9px',
          marginBottom: '18px',
        }}
      >
        <LogoMark showWordmark={false} size={26} />
        <Box
          component="h2"
          id="download-dialog-title"
          sx={{
            margin: 0,
            fontFamily: fonts.display,
            fontWeight: 700,
            fontSize: '15px',
            letterSpacing: '-0.02em',
          }}
        >
          StudioVault Desktop
        </Box>
        <IconButton
          onClick={onClose}
          aria-label="Close"
          size="small"
          sx={{ marginLeft: 'auto', color: t.text.muted }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box
        component="p"
        sx={{
          margin: '0 0 22px',
          fontSize: '14px',
          lineHeight: 1.6,
          color: t.text.secondary,
        }}
      >
        Connects StudioVault to your session folders, compares local files with
        the latest cloud version and uploads changes in parallel. Required for
        sync, version creation and locating projects.
      </Box>

      <DownloadDesktopAppPanel />
    </Dialog>
  );
};

export default DownloadDialog;
